import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { fireConfetti } from '@/lib/perf/confetti';
import { safeError } from '@/lib/safeLog';
import { analytics } from '@/lib/analytics';
import { trackRewardEarned } from '@/lib/analytics/reward-earned';
import { brazeSetAttribute } from '@/lib/analytics/braze';
import { invalidateBalanceQueries } from '@/lib/balanceInvalidation';
import { findNextUnansweredPosition, findPreviousVisiblePosition } from '@/lib/profileWizardOrdering';
import { attributeValueCountsAsAnswer, extractAnswerValue } from '@/lib/answerShapes';
import {
  AGE_RANGE_QUESTION_ID,
  EQUIVALENCE_FLAT_KEYS,
  EQUIVALENT_ANSWERS_FLAG_KEY,
  fetchEquivalentAnswersEnabled,
  type EquivalenceCatalog,
  type EquivalenceSnapshot,
} from '@/lib/questionEquivalence';
import {
  dedupeProfileQuestions,
  fillEquivalentAnswers,
  generateProfileQuestionKey,
} from '@/lib/profileQuestionDedup';
import { isDependencySatisfied, type ProfileAnswerValue } from '@/lib/profileQuestionGating';
import { mapOptionLabelToId, parseQuestionOption } from '@/lib/profileQuestionOptions';
import { persistSurveyAnswers } from '@/lib/persistSurveyAnswers';

// =============================================================================
// Types
// =============================================================================

export interface ProfileStepOption {
  id: string;
  label: string;
  description?: string;
  segment_paths?: string[];
}

export interface ProfileStepField {
  key: string; // Local state key for tracking answers
  question: string;
  type: 'single' | 'multi' | 'text' | 'date';
  options?: ProfileStepOption[];
  placeholder?: string;
  questionMappingId: string; // FK to question_catalog - THIS is stored as attribute_key in user_knowledge
  /** Canonical profile key from question_catalog (e.g. 'ethnicity'). Carried
   * so the equivalence fill can match duplicate ids by fact (contract v3
   * `matchBy`) without hardcoding question text. Null when the row has none. */
  profileAttributeKey?: string | null;
  // REK-1916: conditional gating. When set, this field is only shown once the
  // parent question (`dependsOnQuestionId`) has been answered with one of
  // `dependsOnValues`. Undefined = unconditional.
  dependsOnQuestionId?: string | null;
  dependsOnValues?: string[] | null;
}

export interface ProfileStep {
  id: string; // category slug
  title: string;
  subtitle: string;
  icon: string;
  entriesReward: number;
  fields: ProfileStepField[];
}

// Keep ProfileStepId for backwards compatibility but make it dynamic
export type ProfileStepId = string;

interface StepData {
  [key: string]: string | string[];
}

/** Seed/sync mapping ids (same as REK-2282 backfill). Text is fallback only. */
const WIZARD_FIRST_NAME_MAPPING_ID = '26dc4d83-f640-47b9-b0af-084b20291013';
const WIZARD_LAST_NAME_MAPPING_ID = '83b01edf-8757-4417-b773-16ef941cbdad';

function namePartFromField(field: ProfileStepField): 'first' | 'last' | null {
  if (field.questionMappingId === WIZARD_FIRST_NAME_MAPPING_ID) return 'first';
  if (field.questionMappingId === WIZARD_LAST_NAME_MAPPING_ID) return 'last';
  const q = field.question.trim().toLowerCase();
  if (q === 'first name') return 'first';
  if (q === 'last name') return 'last';
  return null;
}

/** Same compose as persistOnboardingIdentity: trim, drop empties, join on space. */
function composeWizardNameParts(
  fields: Iterable<ProfileStepField>,
  answers: StepData,
): { firstName: string; lastName: string; fullName: string } {
  let firstName = '';
  let lastName = '';
  for (const field of fields) {
    const part = namePartFromField(field);
    if (!part) continue;
    const raw = answers[field.questionMappingId];
    const text = typeof raw === 'string' ? raw.trim() : '';
    if (!text) continue;
    if (part === 'first') firstName = text;
    else lastName = text;
  }
  return {
    firstName,
    lastName,
    fullName: [firstName, lastName].filter(Boolean).join(' '),
  };
}

function isNameField(field: ProfileStepField): boolean {
  return namePartFromField(field) !== null;
}

/** REK-2282: mirror composed First/Last onto profiles.full_name (option 1). */
async function mirrorWizardFullNameToProfile(
  userId: string,
  questionMappingId: string,
  value: string | string[],
  fieldByMappingId: Map<string, ProfileStepField>,
  selectedValues: StepData,
): Promise<void> {
  const field = fieldByMappingId.get(questionMappingId);
  if (!field || !isNameField(field)) return;

  const { firstName, lastName, fullName } = composeWizardNameParts(
    fieldByMappingId.values(),
    { ...selectedValues, [questionMappingId]: value },
  );
  if (!fullName) return;

  // Both parts: always mirror (user finished First+Last). One part: fill NULL
  // only — do not clobber an existing SSO/onboarding full_name with a partial.
  const hasBothParts = Boolean(firstName && lastName);
  const builder = supabase
    .from('profiles')
    .update({ full_name: fullName })
    .eq('id', userId);
  const { error } = await (hasBothParts ? builder : builder.is('full_name', null));
  if (error) {
    safeError('Failed to mirror profiles.full_name from wizard:', error);
  }
}

// =============================================================================
// Category Configuration
// =============================================================================

// Map database categories to 8 display categories for cleaner UI
const CATEGORY_MAPPING: Record<string, string> = {
  // About You (Demographics + Personal)
  'Demographics': 'About You',
  'Personal': 'About You',
  
  // Shopping & Retail
  'Shopping': 'Shopping',
  'Retail': 'Shopping',
  
  // Entertainment & Media
  'Entertainment': 'Entertainment',
  'Media': 'Entertainment',
  'Streaming': 'Entertainment',
  
  // Travel
  'Travel': 'Travel',
  
  // Home & Property
  'Food': 'Home & Living',
  'Property': 'Home & Living',
  'Home': 'Home & Living',
  
  // Finance
  'Finance': 'Finance',
  'Financial': 'Finance',
  
  // Technology
  'Technology': 'Technology',
  'Tech': 'Technology',
  
  // Interests (catch-all for lifestyle categories)
  'Brand': 'Interests',
  'Sports': 'Interests',
  'Health': 'Interests',
  'Lifestyle': 'Interests',
  'Location': 'About You', // Location goes with demographics
  'General': 'Interests',
};

// Fallback for unmapped categories
const DEFAULT_DISPLAY_CATEGORY = 'Interests';

// Get display category from database category
function getDisplayCategory(dbCategory: string): string {
  return CATEGORY_MAPPING[dbCategory] || DEFAULT_DISPLAY_CATEGORY;
}

// Display category configuration with FIXED rewards per step (not per question)
const DISPLAY_CATEGORY_CONFIG: Record<string, { icon: string; subtitle: string; entriesReward: number }> = {
  'About You': {
    icon: 'User',
    subtitle: 'Basic info helps brands send relevant offers.',
    entriesReward: 50,
  },
  'Shopping': {
    icon: 'ShoppingBag',
    subtitle: 'Brands pay well to reach active shoppers.',
    entriesReward: 30,
  },
  'Entertainment': {
    icon: 'Tv',
    subtitle: 'Streaming and media preferences unlock premium surveys.',
    entriesReward: 24,
  },
  'Travel': {
    icon: 'Plane',
    subtitle: 'Travel brands pay top dollar for traveler insights.',
    entriesReward: 30,
  },
  'Home & Living': {
    icon: 'Home',
    subtitle: 'Home and food brands love these insights.',
    entriesReward: 20,
  },
  'Finance': {
    icon: 'DollarSign',
    subtitle: 'Non-sensitive, high-value brand data.',
    entriesReward: 40,
  },
  'Technology': {
    icon: 'Smartphone',
    subtitle: 'Tech brands value these preferences.',
    entriesReward: 24,
  },
  'Interests': {
    icon: 'Target',
    subtitle: 'This is gold for brands — and more rewards for you.',
    entriesReward: 30,
  },
};

// Preferred display category order
const CATEGORY_ORDER: string[] = [
  'About You',
  'Shopping',
  'Entertainment',
  'Travel',
  'Home & Living',
  'Finance',
  'Technology',
  'Interests',
];

// =============================================================================
// Hook to fetch profile questions from database
// =============================================================================

function useProfileQuestionsQuery(userId: string | undefined) {
  // Cache key doesn't include country because it's fetched inside queryFn.
  // Country changes mid-session are extremely rare (set at onboarding);
  // the query re-runs on mount and has a 5-minute staleTime which is acceptable.
  return useQuery({
    queryKey: ['profile-questions-taxonomy', userId],
    enabled: !!userId,
    queryFn: async (): Promise<ProfileStep[]> => {
      // Determine user's country to filter questions appropriately
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('country')
        .eq('id', userId!)
        .single();

      if (profileError) {
        const isAbort = profileError.message?.includes('AbortError') || profileError.message?.includes('signal is aborted');
        if (!isAbort) console.error('Error fetching profile country:', profileError);
        throw profileError;
      }

      const rawCountry = profile?.country?.toUpperCase().trim() || null;
      const userCountry = rawCountry && /^[A-Z]{2}$/.test(rawCountry) ? rawCountry : null;

      // Fetch active questions filtered by user's country (GLOBAL + user's country)
      let query = supabase
        .from('question_catalog')
        .select('*')
        // Stable `id` tie-breaker after priority_score so the de-dup survivor
        // per key is deterministic and matches useProfileProgress (REK-1869).
        .eq('is_active', true)
        .order('priority_score', { ascending: false })
        .order('id', { ascending: true });

      if (userCountry) {
        query = query.or(`country.eq.GLOBAL,country.eq.${userCountry}`);
      } else {
        query = query.eq('country', 'GLOBAL');
      }

      const { data: questions, error } = await query;

      if (error) {
        // Abort errors are transient (Supabase session refresh mid-flight) — throw without
        // logging so TanStack Query retries silently instead of spamming the console.
        const isAbortError = error.message?.includes('AbortError') || error.message?.includes('signal is aborted');
        if (!isAbortError) {
          console.error('Error fetching profile questions:', error);
        }
        throw error;
      }

      if (!questions || questions.length === 0) {
        return [];
      }

      // Deduplicate questions the same way useProfileProgress counts them, via
      // the shared helper, so the wizard and the completion % never disagree
      // about which questions are asked (REK-1869). `questions` is already
      // ordered by priority_score DESC, so the surviving row per key is the one
      // we render.
      const deduplicatedQuestions = dedupeProfileQuestions(questions);

      // Group questions by DISPLAY category (mapped from DB category)
      const categoryMap = new Map<string, ProfileStepField[]>();

      for (const q of deduplicatedQuestions) {
        const dbCategory = q.category || 'General';
        // Map to one of our 8 display categories
        const displayCategory = getDisplayCategory(dbCategory);
        
        // Parse options from JSONB. REK-1917: a bare-string option derives its
        // id from the string itself (via the shared parser) so the stored answer
        // is the readable label, not a generated `opt_<idx>` placeholder.
        let parsedOptions: ProfileStepOption[] = [];
        if (q.options && Array.isArray(q.options)) {
          parsedOptions = q.options.map((opt: unknown, idx: number) => {
            const parsed = parseQuestionOption(opt, idx);
            return {
              id: parsed.id,
              label: parsed.label,
              description: parsed.description,
              segment_paths: parsed.segment_paths,
            };
          });
        }

        // Determine field type
        let fieldType: 'single' | 'multi' | 'text' | 'date' = 'single';
        if (q.question_type === 'multiple_choice') {
          fieldType = 'multi';
        } else if (q.question_type === 'open_ended' || q.question_type === 'text') {
          fieldType = 'text';
        } else if (q.question_type === 'date') {
          fieldType = 'date';
        }

        // Generate a readable key: prefer profile_attribute_key, then generate from question text
        const fieldKey = q.profile_attribute_key || generateProfileQuestionKey(q.question_text || '');
        
        const field: ProfileStepField = {
          key: fieldKey,
          question: q.question_text,
          type: fieldType,
          options: parsedOptions.length > 0 ? parsedOptions : undefined,
          questionMappingId: q.id,
          profileAttributeKey: typeof q.profile_attribute_key === 'string' ? q.profile_attribute_key : null,
          dependsOnQuestionId: q.depends_on_question_id,
          dependsOnValues: q.depends_on_values,
        };

        if (!categoryMap.has(displayCategory)) {
          categoryMap.set(displayCategory, []);
        }
        categoryMap.get(displayCategory)!.push(field);
      }

      // Convert to ProfileStep array with FIXED rewards per category
      const steps: ProfileStep[] = [];
      for (const [displayCategory, fields] of categoryMap) {
        const config = DISPLAY_CATEGORY_CONFIG[displayCategory] || {
          icon: 'HelpCircle',
          subtitle: 'Every answer helps build your profile.',
          entriesReward: 20, // Default if category not configured
        };
        
        steps.push({
          id: displayCategory.toLowerCase().replace(/\s+/g, '_'),
          title: displayCategory,
          subtitle: config.subtitle,
          icon: config.icon,
          entriesReward: config.entriesReward, // Fixed reward per category, not summed
          fields,
        });
      }

      // Sort by preferred category order
      steps.sort((a, b) => {
        const aIndex = CATEGORY_ORDER.indexOf(a.title);
        const bIndex = CATEGORY_ORDER.indexOf(b.title);
        const aOrder = aIndex === -1 ? 999 : aIndex;
        const bOrder = bIndex === -1 ? 999 : bIndex;
        return aOrder - bOrder;
      });

      // Priority ordering (CEO staff-test: ethnicity surfaces first within
      // its step). Gated by the `surveys.priority_order` app_config flag so
      // the existing order is untouched until QA flips it on. Default OFF on
      // missing/error. Dependency gating and skips apply downstream — this
      // only reorders the visible fields.
      try {
        const { data: flagRows } = await supabase
          .from('app_config')
          .select('key, value')
          .in('key', ['surveys.priority_order']);
        const enabled =
          Array.isArray(flagRows) &&
          flagRows.some(
            r =>
              (r as { key?: unknown }).key === 'surveys.priority_order' &&
              String((r as { value?: unknown }).value ?? '')
                .trim()
                .toLowerCase() === 'true',
          );
        if (enabled) {
          for (const step of steps) {
            if (!step.fields.some(f => f.key.trim().toLowerCase() === 'ethnicity')) continue;
            step.fields = [
              ...step.fields.filter(f => f.key.trim().toLowerCase() === 'ethnicity'),
              ...step.fields.filter(f => f.key.trim().toLowerCase() !== 'ethnicity'),
            ];
          }
        }
      } catch {
        // Flag unreadable: keep the existing order.
      }

      return steps;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// =============================================================================
// Context
// =============================================================================

interface ProfileWizardContextType {
  isOpen: boolean;
  currentStepIndex: number;
  currentFieldIndex: number;
  currentStep: ProfileStep | null;
  currentField: ProfileStepField | null;
  selectedValues: StepData;
  isLoading: boolean;
  isSaving: boolean;
  hasError: boolean; // True if questions failed to load
  completedSteps: ProfileStepId[];
  totalSteps: number;
  progress: number;
  totalPotentialEntries: number;
  earnedEntries: number;
  remainingEntries: number;
  profileSteps: ProfileStep[]; // Expose steps for external use
  /**
   * True while the user is editing a specific, already-answered question via
   * `editField` (REK-1913). Consumed by the wizard to suppress its
   * auto-close-on-complete effect so an edit on a 100%-complete profile doesn't
   * immediately close. Cleared after the first `continueToNext` of the edit, and
   * on open/close/skip/reset.
   */
  isEditing: boolean;
  openWizard: () => void;
  /**
   * REK-1913: open the wizard positioned at a SPECIFIC question (by
   * questionMappingId), even if it is already answered, so the user can edit
   * that answer. The edited question shows first (with its current answer
   * pre-selected); the first `continueToNext` afterwards resumes at the global
   * next-unanswered question so the user continues through any remaining
   * unanswered profile questions. No-op if the question is not in the active set.
   */
  editField: (questionMappingId: string) => void;
  closeWizard: () => void;
  selectOption: (optionId: string) => void;
  setTextValue: (value: string) => void;
  continueToNext: () => Promise<void>;
  /**
   * REK-1913: persist the current field's pending value WITHOUT advancing or
   * closing. The wizard calls this when the user dismisses an edit session
   * (click off / X / "Save and finish later") with a valid, non-empty value, so
   * an edited answer is accepted on dismiss for text/date/multi-select fields
   * too (single-choice already saves on selection). Returns true if it saved.
   */
  saveCurrentField: () => Promise<boolean>;
  skipField: () => Promise<void>;
  goBack: () => void;
  canContinue: boolean;
  canGoBack: boolean;
  resetProfile: () => Promise<void>;
  /**
   * Non-destructively re-read `user_knowledge` and recompute answers / progress
   * / remaining entries / resume position. Use when rows were written AFTER the
   * provider's initial load (e.g. the one-click sign-up name/DOB step persists
   * identity fields while the user is still on that screen, before the wizard
   * opens). Unlike `resetProfile`, this deletes nothing.
   */
  reloadProfileData: () => void;
  /**
   * Background, stale-while-revalidate re-read of `user_knowledge` + skip hints.
   * Unlike `reloadProfileData` it does NOT flip the loading flag, so the UI keeps
   * showing already-loaded data and updates in place. Call when returning to the
   * Your Data tab (so cross-surface answers — quick wins / campaign surveys —
   * are reflected) and on wizard open. No-op before the initial load completes.
   */
  refreshProfileData: () => void;
}

const ProfileWizardContext = createContext<ProfileWizardContextType | null>(null);

export function ProfileWizardProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const wizardStartTime = useRef<number | null>(null);
  // REK-1913: edit-session state. `isEditing` is reactive (drives the wizard's
  // auto-close-on-complete guard); `editingRef` is read synchronously inside
  // `continueToNext` (a callback) to decide the post-edit resume target without
  // adding state to its dependency array.
  const [isEditing, setIsEditing] = useState(false);
  const editingRef = useRef(false);

  useEffect(() => {
    if (isOpen) wizardStartTime.current = Date.now();
  }, [isOpen]);

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [currentFieldIndex, setCurrentFieldIndex] = useState(0);
  const [selectedValues, setSelectedValues] = useState<StepData>({});
  const [isSaving, setIsSaving] = useState(false);
  const [completedSteps, setCompletedSteps] = useState<ProfileStepId[]>([]);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  // REK-1811: questionMappingIds the user explicitly skipped. Persisted to
  // public.profile_question_skips (NOT user_knowledge) and used ONLY as a
  // wizard ordering hint -- never as an "answered" signal.
  const [skippedFieldIds, setSkippedFieldIds] = useState<Set<string>>(new Set());

  // Fetch questions from database — only when authenticated
  const { 
    data: profileSteps = [], 
    isLoading: questionsLoading,
    isError: questionsError,
  } = useProfileQuestionsQuery(user?.id);

  const currentStep = profileSteps[currentStepIndex] || null;
  const currentField = currentStep?.fields[currentFieldIndex] || null;

  // REK-1916: flat lookup of every field by its questionMappingId, so gating
  // predicates can resolve a question's dependency config from anywhere.
  const fieldByMappingId = useMemo(() => {
    const map = new Map<string, ProfileStepField>();
    profileSteps.forEach((step) => step.fields.forEach((f) => map.set(f.questionMappingId, f)));
    return map;
  }, [profileSteps]);

  // Mark initial load done when questions finish loading (success or error)
  // This prevents infinite loading state when questions fail or return empty
  useEffect(() => {
    if (!questionsLoading && !initialLoadDone && user?.id) {
      // If questions failed to load or returned empty, mark as done to prevent infinite spinner
      if (questionsError || profileSteps.length === 0) {
        setInitialLoadDone(true);
      }
    }
  }, [questionsLoading, questionsError, profileSteps.length, initialLoadDone, user?.id]);

  // Core read + map of profile answers (user_knowledge) and skip hints
  // (profile_question_skips). Shared by the gated initial load and the
  // background refresh. Skips are a separate, ordering-only store (REK-1811) so
  // they never affect answered-detection, points, payouts, or dedup.
  // When `recomputeCursor` is true it also moves the resume position via the
  // two-pass scan (prefer never-seen, then previously-skipped).
  const applyProfileData = useCallback(
    async ({ recomputeCursor }: { recomputeCursor: boolean }): Promise<void> => {
      if (!user?.id || profileSteps.length === 0) return;

      const [knowledgeRes, skipsRes] = await Promise.all([
        supabase
          .from('user_knowledge')
          .select('attribute_key, attribute_value')
          .eq('user_id', user.id)
          .like('attribute_key', 'survey_qm_%'),
        supabase
          .from('profile_question_skips')
          .select('question_mapping_id')
          .eq('user_id', user.id),
      ]);

      const { data, error } = knowledgeRes;
      if (error) throw error;

      // A failed skip read is non-fatal: skips are only an ordering hint, so
      // fall back to "no skips" (matches pre-REK-1811 behavior) rather than
      // blocking the wizard.
      if (skipsRes.error) {
        safeError('Failed to load profile question skips:', skipsRes.error);
      }
      const skippedIds = new Set<string>(
        (skipsRes.data ?? []).map((row) => row.question_mapping_id),
      );

      const existingData: StepData = {};
      const completed: ProfileStepId[] = [];

      // Flag OFF (default — main behaviour): only a truthy `.selection`
      // counts, stored verbatim. Flag ON: every non-empty prod shape (a–e)
      // counts — legacy `{ answer }`, bare strings, `{ text }`-only — so
      // cross-surface answers are never re-asked here.
      const equivalenceOn = await fetchEquivalentAnswersEnabled(() =>
        supabase
          .from('feature_flags')
          .select('enabled')
          .eq('flag_key', EQUIVALENT_ANSWERS_FLAG_KEY)
          .maybeSingle(),
      ).catch(() => false);
      // attribute_key format: survey_qm_{questionMappingId}. Key existingData by
      // questionMappingId (strip the prefix).
      data?.forEach((item) => {
        if (!equivalenceOn) {
          const value = item.attribute_value as { selection?: string | string[] };
          if (value.selection) {
            const questionMappingId = item.attribute_key.startsWith('survey_qm_')
              ? item.attribute_key.replace('survey_qm_', '')
              : item.attribute_key;
            existingData[questionMappingId] = value.selection;
          }
          return;
        }
        // Flag ON: every non-empty prod shape (a–e) counts — except
        // `{skipped: true}` markers, which campaigns and progress drop
        // before the shape check (contract `unanswered`). Skipped rows stay
        // ordering hints, never answers, on every surface.
        const val = item.attribute_value as { skipped?: unknown } | null | undefined;
        if (val && typeof val === 'object' && !Array.isArray(val) && val.skipped === true) return;
        if (!attributeValueCountsAsAnswer(item.attribute_value)) return;
        const questionMappingId = item.attribute_key.startsWith('survey_qm_')
          ? item.attribute_key.replace('survey_qm_', '')
          : item.attribute_key;
        // Normalize finite numbers/booleans to strings (mirrors
        // useProfileProgress): the shape contract counts `false` as answered
        // and completion checks use truthiness, so a raw boolean would split
        // the two surfaces.
        const extracted = extractAnswerValue(item.attribute_value);
        existingData[questionMappingId] =
          typeof extracted === 'string' || Array.isArray(extracted)
            ? (extracted as string | string[])
            : String(extracted);
      });

      // Equivalence fills (flag-on): a question satisfied by ANY equivalent
      // counts as answered here too — the wizard never re-asks age range
      // when DOB is known, or zip when postal_code is known. Fail-closed:
      // any flat/profile read error keeps exact-id behaviour.
      try {
        if (equivalenceOn) {
          const [{ data: flatRows }, { data: contact }] = await Promise.all([
            supabase
              .from('user_knowledge')
              .select('attribute_key, attribute_value')
              .eq('user_id', user.id)
              .in('attribute_key', [...EQUIVALENCE_FLAT_KEYS]),
            supabase.from('profiles').select('email, phone_number').eq('id', user.id).single(),
          ]);
          const flatAnswersByKey = new Map<string, unknown>();
          const flatKeys = new Set<string>();
          for (const row of flatRows ?? []) {
            const raw = (row as { attribute_value?: unknown }).attribute_value;
            if (!attributeValueCountsAsAnswer(raw)) continue;
            const key = (row as { attribute_key?: string }).attribute_key;
            if (typeof key !== 'string' || key.length === 0) continue;
            flatKeys.add(key);
            flatAnswersByKey.set(key, extractAnswerValue(raw));
          }
          const email = (contact as { email?: unknown } | null)?.email;
          const phone = (contact as { phone_number?: unknown } | null)?.phone_number;
          const snapshot: EquivalenceSnapshot = {
            answeredIds: new Set(Object.keys(existingData)),
            flatKeys,
            hasProfileEmail: typeof email === 'string' && email.trim().length > 0,
            hasProfilePhone: typeof phone === 'string' && phone.trim().length > 0,
          };
          const fieldIds = profileSteps.flatMap((s) => s.fields.map((f) => f.questionMappingId));
          // Generic-match catalog (contract v3 `matchBy`): the rendered
          // fields' embedded keys plus a fill for answered ids no field
          // covers, so unlisted duplicate ids satisfy their twin.
          const questionProfileKeys = new Map<string, string | null>(
            profileSteps.flatMap((s) => s.fields.map((f) => [f.questionMappingId, f.profileAttributeKey ?? null] as const)),
          );
          const uncoveredFieldIds = [...snapshot.answeredIds].filter((id) => !questionProfileKeys.has(id));
          if (uncoveredFieldIds.length > 0) {
            const { data: fillRows } = await supabase
              .from('question_catalog')
              .select('id, profile_attribute_key')
              .in('id', uncoveredFieldIds);
            for (const row of (fillRows ?? []) as Array<{ id: string; profile_attribute_key?: unknown }>) {
              if (typeof row.id === 'string' && !questionProfileKeys.has(row.id)) {
                questionProfileKeys.set(row.id, typeof row.profile_attribute_key === 'string' ? row.profile_attribute_key : null);
              }
            }
          }
          const equivalenceCatalog: EquivalenceCatalog = { questionProfileKeys };
          // Age-range options for the DOB→label derivation: without the
          // configured labels the fill would store a raw DOB where gating
          // compares range labels, hiding every age-gated child.
          const ageField = profileSteps
            .flatMap((s) => s.fields)
            .find((f) => f.questionMappingId === AGE_RANGE_QUESTION_ID);
          // `{ id, text }` pairs so the DOB derivation resolves to the
          // stored-form option id (same as answering the question stores).
          // The post-fill remap below uses the shared helper as a backstop
          // for the same reason the progress hook does (R2-1: the two fills
          // must store the identical value).
          const ageRangeOptions = ageField?.options?.map((o) => ({ id: o.id, text: o.label }));
          const filled = fillEquivalentAnswers(
            fieldIds,
            snapshot,
            new Map(Object.entries(existingData)),
            flatAnswersByKey,
            ageRangeOptions,
            equivalenceCatalog,
          );
          for (const [id, value] of filled) {
            // Shared label→id backstop (same helper as `useProfileProgress`):
            // a derived label stores the catalog option id when the options
            // carry explicit ids, else the label verbatim. Falls back to the
            // input, so an already-stored-form value passes through.
            if (id === AGE_RANGE_QUESTION_ID && typeof value === 'string') {
              existingData[id] = mapOptionLabelToId(ageField?.options, value);
            } else {
              existingData[id] =
                typeof value === 'string' || Array.isArray(value)
                  ? (value as string | string[])
                  : String(value);
            }
          }
        }
      } catch (err) {
        safeError('Equivalent-answers fill failed (exact-id behaviour kept):', err);
      }

      // REK-1916: a gated-out field (its parent answer doesn't qualify) is not
      // presented and must NOT block step completion. Evaluate against the
      // freshly-loaded answers.
      const isGatedOutLocal = (field: ProfileStepField): boolean =>
        !isDependencySatisfied(
          field.dependsOnQuestionId,
          field.dependsOnValues,
          (parentId) => existingData[parentId] as ProfileAnswerValue,
          equivalenceOn,
        );

      profileSteps.forEach((step) => {
        const stepComplete = step.fields.every((field) => {
          if (isGatedOutLocal(field)) return true;
          const val = existingData[field.questionMappingId];
          return val && (Array.isArray(val) ? val.length > 0 : true);
        });
        if (stepComplete) {
          completed.push(step.id);
        }
      });

      setSelectedValues(existingData);
      setCompletedSteps(completed);
      setSkippedFieldIds(skippedIds);

      if (recomputeCursor) {
        // REK-1811: resume on the first NEVER-SEEN question (two-pass), so a
        // user who skipped a question and came back isn't re-prompted for it
        // ahead of questions they've never had a chance to answer. Uses the
        // freshly-loaded maps (not React state, which hasn't committed yet).
        const isAnsweredLocal = (id: string): boolean => {
          const val = existingData[id];
          if (!val) return false;
          if (Array.isArray(val)) return val.length > 0;
          return String(val).trim().length > 0;
        };
        const resumePosition = findNextUnansweredPosition(
          profileSteps,
          0,
          0,
          isAnsweredLocal,
          (id) => skippedIds.has(id),
          (id) => {
            const field = fieldByMappingId.get(id);
            return field ? isGatedOutLocal(field) : false;
          },
        );
        if (resumePosition) {
          setCurrentStepIndex(resumePosition.stepIndex);
          setCurrentFieldIndex(resumePosition.fieldIndex);
        }
      }
    },
    [user?.id, profileSteps, fieldByMappingId],
  );

  // Gated initial load: runs once per provider mount (or after reloadProfileData
  // resets the flag). Shows the loading state (isLoading) until it completes.
  useEffect(() => {
    if (!user?.id || profileSteps.length === 0 || initialLoadDone) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        await applyProfileData({ recomputeCursor: true });
      } catch (err) {
        safeError('Failed to load profile data:', err);
      } finally {
        if (!cancelled) setInitialLoadDone(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id, profileSteps, initialLoadDone, applyProfileData]);

  // Background refresh: re-read user_knowledge + skips WITHOUT flipping the
  // loading flag, so the UI keeps showing the already-loaded (possibly stale)
  // data and updates in place when the fresh read resolves. Used when returning
  // to the Your Data tab and when opening the wizard, so answers made on OTHER
  // surfaces (quick wins via submit-survey-answer, full campaign surveys via
  // submit-campaign-response -- all write the same `survey_qm_<id>` key) are
  // reflected without a skeleton flash or re-prompting an already-answered
  // question. No-op until the initial load has completed (the gated effect owns
  // the first read).
  const refreshProfileData = useCallback(() => {
    if (!user?.id || profileSteps.length === 0 || !initialLoadDone) return;
    void applyProfileData({ recomputeCursor: true }).catch((err) => {
      safeError('Failed to refresh profile data:', err);
    });
  }, [user?.id, profileSteps, initialLoadDone, applyProfileData]);

  // Loading state: only true when actively loading, not when there's an error
  const isLoading = questionsLoading || (!initialLoadDone && !!user?.id && !questionsError);
  
  // Error state: true if questions failed to load
  const hasError = questionsError;

  const selectOption = useCallback((optionId: string) => {
    if (!currentField) return;

    setSelectedValues((prev) => {
      const currentValue = prev[currentField.questionMappingId];

      if (currentField.type === 'single') {
        return { ...prev, [currentField.questionMappingId]: optionId };
      } else if (currentField.type === 'multi') {
        const currentArray = Array.isArray(currentValue) ? currentValue : [];
        const newArray = currentArray.includes(optionId)
          ? currentArray.filter((id) => id !== optionId)
          : [...currentArray, optionId];
        return { ...prev, [currentField.questionMappingId]: newArray };
      }
      return prev;
    });
  }, [currentField]);

  const setTextValue = useCallback((value: string) => {
    if (!currentField || (currentField.type !== 'text' && currentField.type !== 'date')) return;
    setSelectedValues((prev) => ({ ...prev, [currentField.questionMappingId]: value }));
  }, [currentField]);

  const saveFieldToDb = useCallback(async (questionMappingId: string, value: string | string[]) => {
    if (!user?.id) return;

    // REK-1925: survey_qm_* answers are written through the service-role
    // `persist-survey-answers` edge function (a DB trigger now blocks direct
    // client writes to those keys — they gate campaign payout completion). The
    // row shape (survey_qm_{id} = { selection }, source lemonade_wizard) is
    // unchanged; only the writer moved server-side.
    const { error } = await persistSurveyAnswers([
      { questionMappingId, value, source: 'lemonade_wizard' },
    ]);

    if (error) {
      console.error('Failed to save profile data:', error);
      throw new Error(error);
    }

    await mirrorWizardFullNameToProfile(
      user.id,
      questionMappingId,
      value,
      fieldByMappingId,
      selectedValues,
    );
  }, [user?.id, fieldByMappingId, selectedValues]);

  // REK-1811: persist a skip hint. Best-effort -- a failure logs but does not
  // block wizard navigation (worst case the question reappears on next resume,
  // which is the pre-REK-1811 behavior).
  const saveSkipToDb = useCallback(async (questionMappingId: string) => {
    if (!user?.id) return;
    const { error } = await supabase
      .from('profile_question_skips')
      .upsert(
        { user_id: user.id, question_mapping_id: questionMappingId },
        { onConflict: 'user_id,question_mapping_id' },
      );
    if (error) safeError('Failed to persist profile question skip:', error);
  }, [user?.id]);

  // REK-1811: a question that gets answered is no longer "skipped" -- clear the
  // hint so it isn't deprioritized if the user ever re-opens the wizard.
  const clearSkipInDb = useCallback(async (questionMappingId: string) => {
    if (!user?.id) return;
    const { error } = await supabase
      .from('profile_question_skips')
      .delete()
      .eq('user_id', user.id)
      .eq('question_mapping_id', questionMappingId);
    if (error) safeError('Failed to clear profile question skip:', error);
  }, [user?.id]);

  // Helper to check if a field is answered (uses questionMappingId as the key)
  const isFieldAnswered = useCallback((questionMappingId: string): boolean => {
    const val = selectedValues[questionMappingId];
    if (!val) return false;
    if (Array.isArray(val)) return val.length > 0;
    return String(val).trim().length > 0;
  }, [selectedValues]);

  // REK-1916: a field is gated out when its parent dependency isn't satisfied
  // by the current in-session answers. Such fields are never presented.
  const isFieldGatedOut = useCallback((questionMappingId: string): boolean => {
    const field = fieldByMappingId.get(questionMappingId);
    if (!field) return false;
    return !isDependencySatisfied(
      field.dependsOnQuestionId,
      field.dependsOnValues,
      (parentId) => selectedValues[parentId] as ProfileAnswerValue,
    );
  }, [fieldByMappingId, selectedValues]);

  // Find the next question to present from a given position. REK-1811: two-pass
  // ordering prefers never-seen questions over previously-skipped ones (skips
  // are an ordering hint only -- see profileWizardOrdering.ts). REK-1916:
  // conditionally-gated questions are skipped entirely.
  const findNextUnanswered = useCallback((
    startStepIndex: number,
    startFieldIndex: number
  ): { stepIndex: number; fieldIndex: number } | null => {
    return findNextUnansweredPosition(
      profileSteps,
      startStepIndex,
      startFieldIndex,
      isFieldAnswered,
      (id) => skippedFieldIds.has(id),
      isFieldGatedOut,
    );
  }, [profileSteps, isFieldAnswered, skippedFieldIds, isFieldGatedOut]);

  const continueToNext = useCallback(async () => {
    if (!currentStep || !currentField || !user?.id) return;

    const currentValue = selectedValues[currentField.questionMappingId];
    if (!currentValue || (Array.isArray(currentValue) && currentValue.length === 0)) {
      return;
    }

    setIsSaving(true);
    try {
      await saveFieldToDb(currentField.questionMappingId, currentValue);

      // REK-1811: answering clears any prior skip hint for this question.
      if (skippedFieldIds.has(currentField.questionMappingId)) {
        const answeredId = currentField.questionMappingId;
        setSkippedFieldIds((prev) => {
          const next = new Set(prev);
          next.delete(answeredId);
          return next;
        });
        void clearSkipInDb(answeredId);
      }

      // Reflect the new answer in the canonical completion stat LIVE (the My
      // Data ring/% driven by useProfileProgress) on every answer, not just on
      // wizard close. Marks the query stale + refetches for active observers.
      queryClient.invalidateQueries({ queryKey: ['profile-progress'] });

      analytics.trackEvent({
        name: 'profile_field_completed',
        properties: { field_name: currentField.key, step_name: currentStep.id, step_index: currentStepIndex },
      });

      // Find the next UNANSWERED field (skip already answered ones).
      // REK-1913: the task is "edited question up first and then continue with
      // other unanswered profile questions" — so after saving an EDITED question
      // (entered via `editField` from My Data) we keep the user in the wizard and
      // advance to the next unanswered question. Because the edit cursor may sit
      // AFTER some still-unanswered questions, a forward-only scan would miss
      // them; resume from the global start (0,0) so the user continues through
      // ALL remaining unanswered questions (and the wizard closes once none
      // remain). Edit targets are always already-answered, so their step was
      // already completed/awarded — the forward "moved to a new step" award
      // branch is correctly skipped.
      const wasEditNav = editingRef.current;
      if (wasEditNav) {
        editingRef.current = false;
        setIsEditing(false);
      }
      const nextUnanswered = wasEditNav
        ? findNextUnanswered(0, 0)
        : findNextUnanswered(currentStepIndex, currentFieldIndex + 1);

      // REK-1913: an edit that leaves no unanswered questions should simply
      // close the wizard. It must NOT fall through to the first-time completion
      // branch below (which re-fires `onboarding_completed`,
      // `profile_setup_completed`, the Braze "completed" attributes, and
      // `complete-profile-step`) — the profile was already complete before the
      // edit, so re-firing those is wrong. Only a genuine non-edit completion
      // runs that branch.
      if (wasEditNav && !nextUnanswered) {
        setIsOpen(false);
        return;
      }

      if (nextUnanswered) {
        // Check if we're completing the current step (moving to a new step)
        if (nextUnanswered.stepIndex > currentStepIndex) {
          // Award points for completing the current step if not already done
          if (!completedSteps.includes(currentStep.id)) {
            setCompletedSteps((prev) => [...prev, currentStep.id]);
            const newCompletedCount = completedSteps.length + 1;
            const stepPct = profileSteps.length > 0 ? Math.round((newCompletedCount / profileSteps.length) * 100) : 0;
            analytics.trackEvent({
              name: 'onboarding_step_completed',
              properties: { step_name: currentStep.id, step_index: currentStepIndex },
            });
            brazeSetAttribute('profile_completion_pct', stepPct);
            brazeSetAttribute('onboarding_status', 'in_progress');
            
            fireConfetti({
              particleCount: 80,
              spread: 60,
            });

            // REK-1547: server-driven step completion. The
            // complete-profile-step endpoint reads the canonical
            // reward from app_config.profile_step_rewards and mints
            // via award-points service-role. Idempotent on
            // (user, 'profile', `profile_step_${stepId}`).
            try {
              const { data: stepResult } = await supabase.functions.invoke(
                'complete-profile-step',
                { body: { stepId: currentStep.id } },
              );

              // REK-1547 follow-up: emit reward_earned from the client
              // success handler. The server-driven mint path no longer
              // fires usePointsService.awardPoints, so the canonical
              // analytics event would otherwise be dropped.
              trackRewardEarned(stepResult?.pointsAwarded, 'profile');

              // REK-1796: refetch the top-bar balance (and every balance-derived surface).
              invalidateBalanceQueries(queryClient);
            } catch (pointsError) {
              safeError('Failed to award profile points:', pointsError);
              // Don't block profile completion if points fail
            }
          }
        }
        
        setCurrentStepIndex(nextUnanswered.stepIndex);
        setCurrentFieldIndex(nextUnanswered.fieldIndex);
      } else {
        // All fields answered - complete the wizard
        if (!completedSteps.includes(currentStep.id)) {
          setCompletedSteps((prev) => [...prev, currentStep.id]);
          analytics.trackEvent({
            name: 'onboarding_step_completed',
            properties: { step_name: currentStep.id, step_index: currentStepIndex },
          });
          
          fireConfetti({
            particleCount: 80,
            spread: 60,
          });

          try {
            const { data: stepResult } = await supabase.functions.invoke(
              'complete-profile-step',
              { body: { stepId: currentStep.id } },
            );

            // REK-1547 follow-up: emit reward_earned from the client
            // success handler (see above).
            trackRewardEarned(stepResult?.pointsAwarded, 'profile');

            // REK-1796: refetch the top-bar balance (and every balance-derived surface).
            invalidateBalanceQueries(queryClient);
          } catch (pointsError) {
            safeError('Failed to award profile points:', pointsError);
          }
        }

        const completedPct = Math.round(((completedSteps.length + 1) / profileSteps.length) * 100);
        analytics.trackEvent({
          name: 'onboarding_completed',
          properties: {
            time_to_complete: wizardStartTime.current ? Math.round((Date.now() - wizardStartTime.current) / 1000) : undefined,
            profile_completed_pct: Math.min(100, completedPct),
          },
        });
        analytics.trackEvent({ name: 'profile_setup_completed', properties: {} });
        brazeSetAttribute('profile_completion_pct', 100);
        brazeSetAttribute('onboarding_status', 'completed');
        
        setIsOpen(false);
      }
    } catch (err) {
      console.error('Failed to save:', err);
    } finally {
      setIsSaving(false);
    }
  }, [currentStep, currentField, currentFieldIndex, currentStepIndex, selectedValues, completedSteps, saveFieldToDb, user?.id, queryClient, findNextUnanswered, profileSteps.length, skippedFieldIds, clearSkipInDb]);

  // REK-1913: persist the CURRENT field's pending value without advancing or
  // closing. Used by the wizard to commit an in-progress edit when the user
  // dismisses the wizard (click off / X / "Save and finish later") instead of
  // pressing Continue — so "clicking off accepts the change" holds for text /
  // date / multi-select edits too (single-choice already auto-saves on select).
  // No-op when there is no non-empty value. The caller (wizard) is responsible
  // for only invoking this when the value is VALID (text schema / date bounds);
  // this method just persists what it's given. Returns true if a value was saved.
  const saveCurrentField = useCallback(async (): Promise<boolean> => {
    if (!currentField || !user?.id) return false;
    const val = selectedValues[currentField.questionMappingId];
    const hasValue = Array.isArray(val)
      ? val.length > 0
      : typeof val === 'string'
        ? val.trim().length > 0
        : !!val;
    if (!hasValue) return false;

    // Toggle isSaving (like continueToNext) so Continue / option inputs are
    // disabled while this dismiss-save is in flight — prevents a parallel
    // continueToNext from racing this write.
    setIsSaving(true);
    try {
      await saveFieldToDb(currentField.questionMappingId, val);

      // Answering clears any prior skip hint for this question (mirrors continueToNext).
      if (skippedFieldIds.has(currentField.questionMappingId)) {
        const answeredId = currentField.questionMappingId;
        setSkippedFieldIds((prev) => {
          const next = new Set(prev);
          next.delete(answeredId);
          return next;
        });
        void clearSkipInDb(answeredId);
      }

      // Reflect the saved value in the canonical completion stat (My Data ring/%).
      queryClient.invalidateQueries({ queryKey: ['profile-progress'] });
      analytics.trackEvent({
        name: 'profile_field_completed',
        properties: {
          field_name: currentField.key,
          step_name: currentStep?.id,
          step_index: currentStepIndex,
        },
      });
      return true;
    } catch (err) {
      safeError('Failed to save edited field on dismiss:', err);
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [currentField, currentStep?.id, currentStepIndex, selectedValues, saveFieldToDb, user?.id, queryClient, skippedFieldIds, clearSkipInDb]);

  const skipField = useCallback(async () => {
    if (!currentStep || !currentField) return;

    // Skipping ends any edit session — fall back to the normal forward flow.
    editingRef.current = false;
    setIsEditing(false);

    const skippedId = currentField.questionMappingId;

    // Remove any value for this field (it's being skipped)
    setSelectedValues(prev => {
      const next = { ...prev };
      delete next[skippedId];
      return next;
    });

    // REK-1811: record the skip so this question is deprioritized (but still
    // eventually shown) on resume, instead of being re-prompted ahead of
    // never-seen questions. Persisted outside user_knowledge so it never counts
    // as an answer. `nextUnanswered` is computed against the pre-skip set, which
    // is correct: the just-skipped field is behind the forward cursor.
    const nextSkipped = new Set(skippedFieldIds);
    nextSkipped.add(skippedId);
    setSkippedFieldIds(nextSkipped);
    void saveSkipToDb(skippedId);

    // Find the next question to present (prefers never-seen; the just-skipped
    // field sits behind currentFieldIndex + 1 so it is not re-shown now).
    const nextUnanswered = findNextUnanswered(currentStepIndex, currentFieldIndex + 1);

    if (nextUnanswered) {
      setCurrentStepIndex(nextUnanswered.stepIndex);
      setCurrentFieldIndex(nextUnanswered.fieldIndex);
    } else {
      // Nothing left to present forward - close the wizard. Any skipped
      // questions earlier in the list resurface on the next resume.
      setIsOpen(false);
    }
  }, [currentFieldIndex, currentStepIndex, currentStep, currentField, findNextUnanswered, skippedFieldIds, saveSkipToDb]);

  // REK-1916: Back must skip conditionally-gated questions, mirroring forward
  // navigation/resume. Otherwise a user who answered the parent with a
  // disqualifying value (e.g. "No children") could land back on a gated child
  // question that the forward flow never presents.
  const goBack = useCallback(() => {
    const prev = findPreviousVisiblePosition(
      profileSteps,
      currentStepIndex,
      currentFieldIndex,
      isFieldGatedOut,
    );
    if (prev) {
      setCurrentStepIndex(prev.stepIndex);
      setCurrentFieldIndex(prev.fieldIndex);
    }
  }, [currentFieldIndex, currentStepIndex, profileSteps, isFieldGatedOut]);

  const canContinue = currentField
    ? (() => {
        const val = selectedValues[currentField.questionMappingId];
        if (!val) return false;
        if (Array.isArray(val)) return val.length > 0;
        if (typeof val === 'string') return val.trim().length > 0;
        return true;
      })()
    : false;

  // REK-1916: only offer Back when there is an earlier NON-gated question to
  // return to, so `canGoBack` stays consistent with `goBack`'s gated-skip.
  const canGoBack = useMemo(
    () =>
      findPreviousVisiblePosition(profileSteps, currentStepIndex, currentFieldIndex, isFieldGatedOut) !==
      null,
    [profileSteps, currentStepIndex, currentFieldIndex, isFieldGatedOut],
  );

  // Calculate progress
  const totalFields = useMemo(() => 
    profileSteps.reduce((acc, step) => acc + step.fields.length, 0),
    [profileSteps]
  );

  const answeredFieldCount = useMemo(() => 
    profileSteps.reduce((acc, step) => {
      return acc + step.fields.filter(field => {
        const val = selectedValues[field.questionMappingId];
        if (!val) return false;
        if (Array.isArray(val)) return val.length > 0;
        return String(val).trim().length > 0;
      }).length;
    }, 0),
    [profileSteps, selectedValues]
  );

  const progress = totalFields > 0 ? Math.round((answeredFieldCount / totalFields) * 100) : 0;

  const totalPotentialEntries = useMemo(() => 
    profileSteps.reduce((sum, step) => sum + step.entriesReward, 0),
    [profileSteps]
  );

  const earnedEntries = useMemo(() => 
    completedSteps.reduce((sum, stepId) => {
      const step = profileSteps.find(s => s.id === stepId);
      return sum + (step?.entriesReward || 0);
    }, 0),
    [completedSteps, profileSteps]
  );

  const remainingEntries = totalPotentialEntries - earnedEntries;

  const openWizard = useCallback(() => {
    // Normal open is never an edit session; clear any stale edit flag.
    editingRef.current = false;
    setIsEditing(false);
    // Background-refresh answers on open so a question answered on another
    // surface since the last load isn't re-prompted. This does NOT show a
    // loading spinner -- the modal opens immediately on the already-loaded data
    // and updates in place when the fresh read resolves (stale-while-revalidate).
    refreshProfileData();
    setIsOpen(true);
    analytics.trackEvent({
      name: 'profile_wizard_opened',
      properties: {
        completion_pct: progress,
        completed_steps: completedSteps.length,
        total_steps: profileSteps.length,
      },
    });
    brazeSetAttribute('profile_completion_pct', progress);
  }, [progress, completedSteps.length, profileSteps.length, refreshProfileData]);

  // REK-1913: open the wizard at a specific question for editing. Reuses the
  // same "complete profile" wizard UI. Unlike openWizard, this does NOT
  // recompute the resume cursor (which would jump to the next unanswered
  // question) — it positions the cursor on the requested question and keeps it
  // there. A background refresh WITHOUT cursor recompute still pulls in any
  // cross-surface answers without moving the user off the question they chose
  // to edit.
  const editField = useCallback((questionMappingId: string) => {
    let target: { stepIndex: number; fieldIndex: number } | null = null;
    for (let si = 0; si < profileSteps.length && !target; si++) {
      const fi = profileSteps[si].fields.findIndex(
        (f) => f.questionMappingId === questionMappingId,
      );
      if (fi !== -1) target = { stepIndex: si, fieldIndex: fi };
    }
    // The question isn't in the active set (e.g. answered via a now-inactive or
    // country-filtered campaign question) — nothing to edit here.
    if (!target) return;

    editingRef.current = true;
    setIsEditing(true);
    // Pull cross-surface answers, but DON'T move the cursor off the edit target.
    void applyProfileData({ recomputeCursor: false }).catch((err) => {
      safeError('Failed to refresh profile data for edit:', err);
    });
    setCurrentStepIndex(target.stepIndex);
    setCurrentFieldIndex(target.fieldIndex);
    setIsOpen(true);

    analytics.trackEvent({
      name: 'profile_wizard_opened',
      properties: {
        completion_pct: progress,
        completed_steps: completedSteps.length,
        total_steps: profileSteps.length,
      },
    });
    brazeSetAttribute('profile_completion_pct', progress);
  }, [profileSteps, applyProfileData, progress, completedSteps.length]);

  const closeWizard = useCallback(() => {
    editingRef.current = false;
    setIsEditing(false);
    const allDone = completedSteps.length === profileSteps.length && profileSteps.length > 0;
    if (!allDone && wizardStartTime.current) {
      analytics.trackEvent({
        name: 'profile_wizard_abandoned',
        properties: {
          completion_pct: progress,
          completed_steps: completedSteps.length,
          total_steps: profileSteps.length,
          time_spent_ms: Date.now() - wizardStartTime.current,
        },
      });
    }
    setIsOpen(false);
  }, [completedSteps.length, profileSteps.length, progress]);

  const resetProfile = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      // REK-1811: clear answers AND skip hints so a reset truly starts fresh.
      await Promise.all([
        supabase
          .from('user_knowledge')
          .delete()
          .eq('user_id', user.id)
          .like('attribute_key', 'survey_qm_%'),
        supabase
          .from('profile_question_skips')
          .delete()
          .eq('user_id', user.id),
      ]);

      setSelectedValues({});
      setCompletedSteps([]);
      setSkippedFieldIds(new Set());
      setCurrentStepIndex(0);
      setCurrentFieldIndex(0);
      editingRef.current = false;
      setIsEditing(false);
      setInitialLoadDone(false); // Allow reloading
    } catch (err) {
      safeError('Failed to reset profile:', err);
    }
  }, [user?.id]);

  // Re-trigger loadExistingData (which is gated on initialLoadDone) to re-read
  // user_knowledge without deleting anything. Used after the one-click sign-up
  // name/DOB step writes rows post-initial-load so progress/remainingEntries
  // and the wizard's resume cursor reflect them (REK-1730 QA).
  const reloadProfileData = useCallback(() => {
    setInitialLoadDone(false);
  }, []);

  const value: ProfileWizardContextType = {
    isOpen,
    currentStepIndex,
    currentFieldIndex,
    currentStep,
    currentField,
    selectedValues,
    isLoading,
    isSaving,
    hasError,
    completedSteps,
    totalSteps: profileSteps.length,
    progress,
    totalPotentialEntries,
    earnedEntries,
    remainingEntries,
    profileSteps, // Expose for external components
    isEditing,
    openWizard,
    editField,
    closeWizard,
    selectOption,
    setTextValue,
    continueToNext,
    saveCurrentField,
    skipField,
    goBack,
    canContinue,
    canGoBack,
    resetProfile,
    reloadProfileData,
    refreshProfileData,
  };

  return (
    <ProfileWizardContext.Provider value={value}>
      {children}
    </ProfileWizardContext.Provider>
  );
}

export function useProfileWizard(): ProfileWizardContextType {
  const context = useContext(ProfileWizardContext);
  if (!context) {
    throw new Error('useProfileWizard must be used within a ProfileWizardProvider');
  }
  return context;
}

// =============================================================================
// Backwards compatibility export
// =============================================================================
// Components that imported PROFILE_STEPS can now use profileSteps from context
// This is a placeholder that will be empty - components should migrate to use
// the profileSteps from useProfileWizard() instead
export const PROFILE_STEPS: ProfileStep[] = [];
